"use client";

import React, { useEffect, useState, useRef, useCallback } from "react";
import { MapPin, Phone, Mail, Building2, Clock, Upload, X, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Loader as Loader2, FileImage, ArrowRight } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
interface FormOption {
  value: string;
  label: string;
}
interface FormFieldDefinition {
  id: string;
  key: string;
  type: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  options?: FormOption[];
  config?: Record<string, any>;
  validation?: {
    messages?: {
      required?: string;
      invalid?: string;
    };
  };
}
interface FormDefinition {
  id: number;
  key: string;
  name: string;
  availability?: {
    accepting: boolean;
    message?: string;
  };
  fields: FormFieldDefinition[];
  submit?: {
    label: string;
    processing_label: string;
  };
  spam: {
    token: string;
    honeypot?: {
      field: string;
    };
    min_seconds?: number;
  };
}
interface UploadedFileItem {
  file_id: string;
  name: string;
  size: number;
}
export default function Kontakt() {
  const prefersReducedMotion = useReducedMotion();

  // wvc-forms state
  const [formDef, setFormDef] = useState<FormDefinition | null>(null);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">("loading");
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFileItem[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [hpValue, setHpValue] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, {
    message: string;
  }>>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState<string | null>(null);

  // Spam protection timer
  const fetchTimestampRef = useRef<number>(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Fetch form definition
  const loadForm = useCallback(async () => {
    setLoadState("loading");
    setGeneralError(null);
    const client = getWvcClient();
    if (!client?.pluginRequest) {
      setLoadState("error");
      return;
    }
    try {
      let res = await client.pluginRequest("wvc-forms/v1/forms/quote-request");
      fetchTimestampRef.current = Date.now();
      setFormDef(res);
      setLoadState("ready");
    } catch (err: any) {
      if (err?.status === 404) {
        try {
          const listRes = await client.pluginRequest("wvc-forms/v1/forms");
          const items: Array<{
            key: string;
            name: string;
          }> = listRes?.items || [];
          const match = items.find(item => item.key === "quote-request") || (items.length === 1 ? items[0] : null) || items.find(item => item.name.toLowerCase().includes("wycen") || item.name.toLowerCase().includes("kontakt") || item.name.toLowerCase().includes("quote"));
          if (match) {
            const fallbackRes = await client.pluginRequest(`wvc-forms/v1/forms/${match.key}`);
            fetchTimestampRef.current = Date.now();
            setFormDef(fallbackRes);
            setLoadState("ready");
            return;
          }
        } catch {
          // fallback failed
        }
      }
      setLoadState("error");
    }
  }, []);
  useEffect(() => {
    loadForm();
  }, [loadForm]);

  // Map fields by expected key or standard fallbacks
  const getField = (targetKey: string): FormFieldDefinition | undefined => {
    if (!formDef?.fields) return undefined;
    return formDef.fields.find(f => f.key === targetKey);
  };
  const nameField = getField("imie") || getField("name") || getField("f_name") || formDef?.fields.find(f => f.type === "text" && f.label.toLowerCase().includes("imię"));
  const phoneField = getField("telefon") || getField("phone") || formDef?.fields.find(f => f.type === "phone" || f.label.toLowerCase().includes("telefon"));
  const emailField = getField("email") || getField("e-mail") || formDef?.fields.find(f => f.type === "email");
  const serviceField = getField("usluga") || getField("service") || formDef?.fields.find(f => f.type === "select" || f.options?.length);
  const locationField = getField("lokalizacja") || getField("location") || formDef?.fields.find(f => f.key.includes("lokal") || f.label.toLowerCase().includes("lokalizacj"));
  const areaField = getField("powierzchnia") || getField("area") || formDef?.fields.find(f => f.type === "number" || f.label.toLowerCase().includes("powierzchnia"));
  const messageField = getField("wiadomosc") || getField("message") || formDef?.fields.find(f => f.type === "textarea");
  const fileField = getField("pliki") || getField("zdjecia") || formDef?.fields.find(f => f.type === "file");
  const handleInputChange = (fieldId: string, val: string) => {
    setFormValues(prev => ({
      ...prev,
      [fieldId]: val
    }));
    if (fieldErrors[fieldId]) {
      setFieldErrors(prev => {
        const next = {
          ...prev
        };
        delete next[fieldId];
        return next;
      });
    }
  };

  // Handle image attachment upload
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !formDef) return;
    setUploadError(null);
    setIsUploading(true);
    const client = getWvcClient();
    if (!client?.pluginUpload) {
      setUploadError("Przesyłanie plików nie jest wspierane w tym trybie.");
      setIsUploading(false);
      return;
    }
    const fieldTargetId = fileField ? fileField.id : "f_files";
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const validTypes = ["image/jpeg", "image/png", "image/webp"];
      if (!validTypes.includes(file.type)) {
        setUploadError("Akceptowane formaty zdjęć to wyłącznie JPG, PNG oraz WEBP.");
        continue;
      }
      try {
        const res = await client.pluginUpload(`wvc-forms/v1/forms/${formDef.key}/upload`, {
          file,
          fields: {
            token: formDef.spam.token,
            field: fieldTargetId
          }
        });
        if (res && res.success && res.file_id) {
          setUploadedFiles(prev => [...prev, {
            file_id: res.file_id,
            name: res.name || file.name,
            size: res.size || file.size
          }]);
        } else {
          setUploadError(res?.message || "Nie udało się przesłać wybranego pliku.");
        }
      } catch (err: any) {
        setUploadError(err?.message || "Błąd podczas przesyłania załącznika.");
      }
    }
    setIsUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  const removeFile = (fileId: string) => {
    setUploadedFiles(prev => prev.filter(item => item.file_id !== fileId));
  };

  // Form Submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formDef || isSubmitting) return;
    setGeneralError(null);
    setFieldErrors({});

    // Min seconds validation
    const minSeconds = formDef.spam.min_seconds || 3;
    const elapsedSeconds = (Date.now() - fetchTimestampRef.current) / 1000;
    if (elapsedSeconds < minSeconds) {
      await new Promise(resolve => setTimeout(resolve, Math.ceil((minSeconds - elapsedSeconds) * 1000)));
    }
    setIsSubmitting(true);
    const client = getWvcClient();

    // Prepare payload
    const submissionValues: Record<string, any> = {
      ...formValues
    };
    if (fileField && uploadedFiles.length > 0) {
      submissionValues[fileField.id] = uploadedFiles.map(f => f.file_id);
    }
    const payload: any = {
      values: submissionValues,
      token: formDef.spam.token,
      meta: {
        source_url: typeof window !== "undefined" ? window.location.href : "",
        referrer: typeof document !== "undefined" ? document.referrer : ""
      }
    };
    if (formDef.spam.honeypot) {
      payload.hp = {
        [formDef.spam.honeypot.field]: hpValue
      };
    }
    try {
      const res = await client.pluginRequest(`wvc-forms/v1/forms/${formDef.key}/submit`, {
        method: "POST",
        data: payload
      });
      if (res.success) {
        if (res.confirmation?.type === "redirect" && res.confirmation.url) {
          window.location.assign(res.confirmation.url);
        } else {
          setSubmissionSuccess(res.confirmation?.message || "Zapytanie zostało przyjęte. Szczegóły projektu zostały przekazane bezpośrednio do biuro@budsys.pl.");
        }
      } else if (res.code === "frms_validation_failed") {
        setFieldErrors(res.errors || {});
        setGeneralError("Formularz zawiera błędy. Uzupełnij wymagane pozycje.");
      } else if (res.code === "frms_invalid_token") {
        setGeneralError("Sesja wygasła. Odświeżanie parametrów formularza...");
        await loadForm();
      } else {
        setGeneralError(res.message || "Wystąpił problem z przesłaniem formularza.");
      }
    } catch (err: any) {
      setGeneralError(err?.message || "Błąd połączenia. Spróbuj ponownie za chwilę.");
    } finally {
      setIsSubmitting(false);
    }
  };
  const defaultServiceOptions = [{
    value: "budowa_domu",
    label: "Budowa domu"
  }, {
    value: "fundamenty",
    label: "Fundamenty"
  }, {
    value: "prace_ziemne",
    label: "Prace ziemne"
  }, {
    value: "mikrocement",
    label: "Mikrocement"
  }, {
    value: "zywica_epoksyd",
    label: "Żywica / Epoksyd"
  }, {
    value: "posadzki_dekoracyjne",
    label: "Posadzki dekoracyjne"
  }, {
    value: "inne",
    label: "Inne"
  }];
  const serviceOptions = serviceField?.options && serviceField.options.length > 0 ? serviceField.options : defaultServiceOptions;
  return <section id="kontakt" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden border-t border-border/80">
      {/* Structural Datum hairline */}
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-80" />

      <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-16">
        {/* Header Block */}
        <div className="mb-14 md:mb-18 max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-border bg-secondary/80 text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
            <span className="size-2 rounded-full bg-accent" />
            Wycena i kontakt bezpośredni
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-sans">
            Rozpocznij projekt z solidnym wykonawcą.
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground font-light leading-relaxed">
            Skontaktuj się z naszym zespołem inżynieryjnym. Przygotujemy rzetelną kalkulację kosztów dla budowy domu, prac ziemnych lub bezspoinowych posadzek technicznych.
          </p>
        </div>

        {/* 7:5 Asymmetric Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left Column (7 cols): Estimation Form */}
          <motion.div initial={prefersReducedMotion ? false : {
          opacity: 0,
          y: 16
        }} whileInView={prefersReducedMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          ease: "easeOut"
        }} className="lg:col-span-7 bg-card/80 backdrop-blur-md rounded-xl border border-border p-6 md:p-10 shadow-sm relative">
            {/* Architectural accent notch */}
            <div className="absolute top-0 right-8 w-8 h-0.5 bg-accent" />

            <div className="mb-8 border-b border-border/70 pb-5">
              <h3 className="text-xl md:text-2xl font-bold text-foreground font-sans">
                Formularz zapytania ofertowego
              </h3>
              <p className="text-sm text-muted-foreground mt-1 font-light">
                Wypełnij parametry inwestycji. Zgłoszenia trafiają wprost do działu wycen.
              </p>
            </div>

            {submissionSuccess ? <div className="py-12 px-6 text-center space-y-4">
                <div className="size-14 mx-auto rounded-full bg-accent/20 flex items-center justify-center text-foreground">
                  <CheckCircle2 className="size-8 text-accent-foreground" />
                </div>
                <h4 className="text-xl font-bold text-foreground font-sans">
                  Zapytanie zostało pomyślnie wysłane
                </h4>
                <div className="text-sm text-muted-foreground font-light max-w-md mx-auto leading-relaxed" dangerouslySetInnerHTML={{
              __html: submissionSuccess
            }} />
                <div className="pt-4">
                  <Button variant="outline" onClick={() => {
                setSubmissionSuccess(null);
                setFormValues({});
                setUploadedFiles([]);
              }} className="border-border text-foreground hover:bg-secondary">
                    Wyślij kolejne zapytanie
                  </Button>
                </div>
              </div> : loadState === "loading" ? <div className="py-20 flex flex-col items-center justify-center space-y-3 text-muted-foreground">
                <Loader2 className="size-8 animate-spin text-accent" />
                <p className="text-sm font-light">Inicjalizacja modułu kalkulacji...</p>
              </div> : loadState === "error" && !formDef ? <div className="py-12 px-6 text-center space-y-4 bg-secondary/50 rounded-lg border border-border">
                <AlertCircle className="size-8 mx-auto text-destructive" />
                <p className="text-sm text-muted-foreground">
                  Moduł formularza jest chwilowo niedostępny. Prosimy o bezpośredni kontakt telefoniczny lub mailowy.
                </p>
                <div className="flex flex-wrap justify-center gap-4 pt-2">
                  <Button asChild variant="outline" className="border-border">
                    <a href="tel:796280315">Zadzwoń: 796 280 315</a>
                  </Button>
                  <Button asChild variant="outline" className="border-border">
                    <a href="mailto:biuro@budsys.pl">biuro@budsys.pl</a>
                  </Button>
                </div>
              </div> : <form onSubmit={handleSubmit} className="space-y-6">
                {/* General server error */}
                {generalError && <div className="p-4 rounded-md bg-destructive/10 border border-destructive/30 text-destructive text-sm flex items-start gap-2">
                    <AlertCircle className="size-4 shrink-0 mt-0.5" />
                    <span>{generalError}</span>
                  </div>}

                {/* Honeypot Decoy input */}
                {formDef?.spam.honeypot && <div aria-hidden="true" style={{
              position: "absolute",
              left: "-9999px",
              top: "-9999px"
            }}>
                    <input type="text" name={formDef.spam.honeypot.field} tabIndex={-1} autoComplete="off" value={hpValue} onChange={e => setHpValue(e.target.value)} />
                  </div>}

                {/* Row 1: Imię & Telefon */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="input-name" className="text-xs uppercase font-medium tracking-wide text-muted-foreground">
                      {nameField?.label || "Imię i nazwisko"}
                      {nameField?.required && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    <Input id="input-name" type="text" required={nameField?.required ?? true} placeholder={nameField?.placeholder || "Jan Kowalski"} value={nameField ? formValues[nameField.id] || "" : ""} onChange={e => nameField && handleInputChange(nameField.id, e.target.value)} className="bg-background/90 border-border focus-visible:border-accent text-foreground" />
                    {nameField && fieldErrors[nameField.id] && <p className="text-xs text-destructive">{fieldErrors[nameField.id].message}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="input-phone" className="text-xs uppercase font-medium tracking-wide text-muted-foreground">
                      {phoneField?.label || "Telefon"}
                      {phoneField?.required && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    <Input id="input-phone" type="tel" required={phoneField?.required ?? true} placeholder={phoneField?.placeholder || "np. 796 280 315"} value={phoneField ? formValues[phoneField.id] || "" : ""} onChange={e => phoneField && handleInputChange(phoneField.id, e.target.value)} className="bg-background/90 border-border focus-visible:border-accent text-foreground font-mono text-sm" />
                    {phoneField && fieldErrors[phoneField.id] && <p className="text-xs text-destructive">{fieldErrors[phoneField.id].message}</p>}
                  </div>
                </div>

                {/* Row 2: E-mail & Usługa */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="input-email" className="text-xs uppercase font-medium tracking-wide text-muted-foreground">
                      {emailField?.label || "Adres e-mail"}
                      {emailField?.required && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    <Input id="input-email" type="email" required={emailField?.required ?? true} placeholder={emailField?.placeholder || "adres@domena.pl"} value={emailField ? formValues[emailField.id] || "" : ""} onChange={e => emailField && handleInputChange(emailField.id, e.target.value)} className="bg-background/90 border-border focus-visible:border-accent text-foreground" />
                    {emailField && fieldErrors[emailField.id] && <p className="text-xs text-destructive">{fieldErrors[emailField.id].message}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="select-service" className="text-xs uppercase font-medium tracking-wide text-muted-foreground">
                      {serviceField?.label || "Zakres prac"}
                      {serviceField?.required && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    <Select value={serviceField ? formValues[serviceField.id] || "" : ""} onValueChange={val => serviceField && handleInputChange(serviceField.id, val)}>
                      <SelectTrigger id="select-service" className="w-full bg-background/90 border-border focus-visible:border-accent text-foreground">
                        <SelectValue placeholder="Wybierz usługę" />
                      </SelectTrigger>
                      <SelectContent className="bg-popover text-popover-foreground border-border">
                        {serviceOptions.map((opt, optIndex) => <SelectItem key={opt.value || optIndex} value={opt.value} data-index={optIndex}>
                            {opt.label}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                    {serviceField && fieldErrors[serviceField.id] && <p className="text-xs text-destructive">{fieldErrors[serviceField.id].message}</p>}
                  </div>
                </div>

                {/* Row 3: Lokalizacja & Powierzchnia */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="input-location" className="text-xs uppercase font-medium tracking-wide text-muted-foreground">
                      {locationField?.label || "Lokalizacja inwestycji"}
                      {locationField?.required && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    <Input id="input-location" type="text" placeholder={locationField?.placeholder || "Miejscowość / powiat"} value={locationField ? formValues[locationField.id] || "" : ""} onChange={e => locationField && handleInputChange(locationField.id, e.target.value)} className="bg-background/90 border-border focus-visible:border-accent text-foreground" />
                    {locationField && fieldErrors[locationField.id] && <p className="text-xs text-destructive">{fieldErrors[locationField.id].message}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="input-area" className="text-xs uppercase font-medium tracking-wide text-muted-foreground">
                      {areaField?.label || "Powierzchnia w m²"}
                      {areaField?.required && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    <Input id="input-area" type="number" step="any" placeholder={areaField?.placeholder || "np. 150"} value={areaField ? formValues[areaField.id] || "" : ""} onChange={e => areaField && handleInputChange(areaField.id, e.target.value)} className="bg-background/90 border-border focus-visible:border-accent text-foreground font-mono text-sm" />
                    {areaField && fieldErrors[areaField.id] && <p className="text-xs text-destructive">{fieldErrors[areaField.id].message}</p>}
                  </div>
                </div>

                {/* Row 4: Wiadomość */}
                <div className="space-y-2">
                  <Label htmlFor="input-message" className="text-xs uppercase font-medium tracking-wide text-muted-foreground">
                    {messageField?.label || "Opis projektu / uwagi techniczne"}
                    {messageField?.required && <span className="text-destructive ml-1">*</span>}
                  </Label>
                  <Textarea id="input-message" rows={4} placeholder={messageField?.placeholder || "Podaj kluczowe informacje, np. stan podłoża, planowany termin realizacji, preferowany rodzaj wykończenia..."} value={messageField ? formValues[messageField.id] || "" : ""} onChange={e => messageField && handleInputChange(messageField.id, e.target.value)} className="bg-background/90 border-border focus-visible:border-accent text-foreground resize-y" />
                  {messageField && fieldErrors[messageField.id] && <p className="text-xs text-destructive">{fieldErrors[messageField.id].message}</p>}
                </div>

                {/* Row 5: File upload dropzone */}
                <div className="space-y-2">
                  <Label className="text-xs uppercase font-medium tracking-wide text-muted-foreground flex justify-between items-center">
                    <span>Załączniki projektowe i zdjęcia (.jpg, .png, .webp)</span>
                    <span className="text-muted-foreground/80 lowercase">opcjonalnie</span>
                  </Label>

                  <div onClick={() => fileInputRef.current?.click()} className="group border border-dashed border-border hover:border-accent/80 rounded-lg p-5 text-center cursor-pointer transition-colors bg-secondary/30 hover:bg-secondary/60 flex flex-col items-center justify-center space-y-2">
                    <input ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp" multiple className="hidden" onChange={handleFileSelect} />
                    <div className="size-10 rounded-full bg-background flex items-center justify-center text-muted-foreground group-hover:text-foreground transition-colors shadow-2xs">
                      {isUploading ? <Loader2 className="size-5 animate-spin text-accent" /> : <Upload className="size-5" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        Kliknij lub przeciągnij rzuty, zdjęcia stanu obecnego
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Maksymalny rozmiar pliku: 10 MB. Akceptowane: JPG, PNG, WEBP.
                      </p>
                    </div>
                  </div>

                  {uploadError && <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                      <AlertCircle className="size-3.5" />
                      {uploadError}
                    </p>}

                  {/* Uploaded chips */}
                  {uploadedFiles.length > 0 && <div className="flex flex-wrap gap-2 pt-2">
                      {uploadedFiles.map((file, fileIdx) => <div key={file.file_id || fileIdx} data-index={fileIdx} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-secondary border border-border text-xs text-foreground font-mono">
                          <FileImage className="size-3.5 text-muted-foreground" />
                          <span className="truncate max-w-[140px]">{file.name}</span>
                          <span className="text-muted-foreground">({Math.round(file.size / 1024)} KB)</span>
                          <Button type="button" variant="ghost" size="icon" onClick={ev => {
                    ev.stopPropagation();
                    removeFile(file.file_id);
                  }} className="size-4 p-0 text-muted-foreground hover:text-destructive rounded-none">
                            <X className="size-3" />
                          </Button>
                        </div>)}
                    </div>}
                </div>

                {/* Submit action */}
                <div className="pt-3">
                  <Button type="submit" disabled={isSubmitting} className="w-full h-12 bg-accent text-accent-foreground font-semibold hover:bg-accent/90 border border-accent/40 rounded-md transition-[background-color,box-shadow] shadow-sm hover:shadow-md flex items-center justify-center gap-2">
                    {isSubmitting ? <>
                        <Loader2 className="size-4 animate-spin" />
                        <span>Przetwarzanie zapytania...</span>
                      </> : <>
                        <span>{formDef?.submit?.label || "Wyślij zapytanie o wycenę"}</span>
                        <ArrowRight className="size-4" />
                      </>}
                  </Button>
                  <p className="text-xs text-muted-foreground text-center mt-3 font-light">
                    Twoje dane są przetwarzane wyłącznie w celu przygotowania oferty technicznej.
                  </p>
                </div>
              </form>}
          </motion.div>

          {/* Right Column (5 cols): Verified Corporate Credentials & Map */}
          <motion.div initial={prefersReducedMotion ? false : {
          opacity: 0,
          y: 16
        }} whileInView={prefersReducedMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: 0.1,
          ease: "easeOut"
        }} className="lg:col-span-5 space-y-6">
            {/* Corporate Details Card */}
            <Card className="border border-border bg-card shadow-xs rounded-xl overflow-hidden py-0">
              <div className="p-6 md:p-8 space-y-6">
                <div>
                  <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                    <Building2 className="size-3.5 text-accent" />
                    Dane formalne
                  </div>
                  <h3 className="text-2xl font-bold text-foreground font-sans tracking-tight">
                    Budsys.pl sp. z o.o.
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Generalne wykonawstwo budowlane i posadzki techniczne
                  </p>
                </div>

                <div className="h-px bg-border/80" />

                {/* Contact Items List */}
                <div className="space-y-4">
                  {/* Address */}
                  <div className="flex items-start gap-3.5">
                    <div className="size-9 rounded-md bg-secondary flex items-center justify-center text-foreground shrink-0 mt-0.5 border border-border">
                      <MapPin className="size-4 text-foreground" />
                    </div>
                    <div>
                      <div className="text-xs uppercase font-medium text-muted-foreground tracking-wide">
                        Adres rejestrowy i baza
                      </div>
                      <div className="text-base font-semibold text-foreground mt-0.5">
                        Bojszowy Nowe, Śląsk
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 font-light">
                        Polska
                      </div>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex items-start gap-3.5">
                    <div className="size-9 rounded-md bg-secondary flex items-center justify-center text-foreground shrink-0 mt-0.5 border border-border">
                      <Phone className="size-4 text-foreground" />
                    </div>
                    <div>
                      <div className="text-xs uppercase font-medium text-muted-foreground tracking-wide">
                        Infolinia techniczna
                      </div>
                      <a href="tel:796280315" className="text-lg font-bold text-foreground hover:text-accent font-mono transition-colors block mt-0.5">+48 796 280 315</a>
                      <div className="text-xs text-muted-foreground mt-0.5 font-light">
                        Pn - Pt: 7:00 - 18:00
                      </div>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start gap-3.5">
                    <div className="size-9 rounded-md bg-secondary flex items-center justify-center text-foreground shrink-0 mt-0.5 border border-border">
                      <Mail className="size-4 text-foreground" />
                    </div>
                    <div>
                      <div className="text-xs uppercase font-medium text-muted-foreground tracking-wide">
                        Adres poczty elektronicznej
                      </div>
                      <a href="mailto:biuro@budsys.pl" className="text-base font-semibold text-foreground hover:text-accent transition-colors block mt-0.5">
                        biuro@budsys.pl
                      </a>
                      <div className="text-xs text-muted-foreground mt-0.5 font-light">
                        Bezpośredni kontakt do inżynierów projektu
                      </div>
                    </div>
                  </div>
                </div>

                <div className="h-px bg-border/80" />

                {/* Territorial Coverage Badges */}
                <div className="space-y-2 pt-1">
                  <div className="text-xs uppercase font-medium text-muted-foreground tracking-wide">
                    Zasięg operacyjny
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-secondary/80 border border-border text-xs font-medium text-foreground">
                      <span className="size-2 rounded-full bg-accent shrink-0" />
                      <span>Budowa i ziemne: Bielsko-Biała (+25 km)</span>
                    </div>
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-secondary/80 border border-border text-xs font-medium text-foreground">
                      <span className="size-2 rounded-full bg-accent shrink-0" />
                      <span>Posadzki: Cała Polska</span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Embedded Location Map Card */}
            <Card className="border border-border bg-card shadow-xs rounded-xl overflow-hidden py-0">
              <div className="p-4 border-b border-border flex items-center justify-between bg-secondary/30">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-foreground">
                  <MapPin className="size-4 text-accent" />
                  Lokalizacja bazy operacyjnej
                </div>
                <span className="text-xs font-mono text-muted-foreground">Bojszowy Nowe</span>
              </div>
              <div className="relative w-full h-64 md:h-72 bg-muted overflow-hidden">
                {/* Clean embedded map centered on Bojszowy Nowe */}
                <iframe title="Lokalizacja BUDSYS w Bojszowy Nowe" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d40998.423788772395!2d19.065278!3d50.046944!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47169623e8081f9f%3A0x9d54e4c29cfc0b62!2sBojszowy%20Nowe!5e0!3m2!1spl!2spl!4v1700000000000!5m2!1spl!2spl" className="w-full h-full border-0 grayscale contrast-125 opacity-90 hover:opacity-100 hover:grayscale-0 transition-all duration-300" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                <div className="absolute bottom-3 left-3 bg-background/90 backdrop-blur-md px-3 py-1.5 rounded border border-border text-xs font-mono shadow-xs">
                  50°02&apos;49.0&quot;N 19°05&apos;22.0&quot;E
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}